import React from 'react';
import { useDispatch } from 'react-redux';
import isString from 'lodash-es/isString';
import cx from 'classnames';
import {
  Dialog,
  DialogProps,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import { Box, IconButton, units } from '@ha/dsl';
import { track } from 'ha/modules/Analytics/actions';
import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { CloseIcon } from 'ha/ui/next/Icons';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

interface Props {
  classes?: DialogProps['classes'];
  /** TODO: Replace by "open" prop. */
  show: boolean;
  open?: boolean;
  /** Name of the Dialog. Used to track "Open dialog" events. */
  name?: string;
  onClose: () => void;
  onEnter?: () => void;
  title?: string | JSX.Element;
  content?: string | React.ReactNode;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  dialogActions?: JSX.Element;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  fullScreen?: boolean;
  dividers?: boolean;
  noContentPadding?: boolean;
  keepMounted?: boolean;
  PaperProps?: DialogProps['PaperProps'];
  dataTestLocator?: string;
}

const useStyles = makeStyles(theme => ({
  actions: {
    padding: units.yx(1, 1.5),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  noPadding: {
    padding: 0,
  },
}));

export const DialogNext: React.FC<Props> = ({
  show: showProp,
  open: openProp,
  name,
  onClose,
  onEnter = undefined,
  title,
  content,
  maxWidth,
  dialogActions,
  fullScreen,
  dividers = false,
  noContentPadding = false,
  dataTestLocator,
  ...props
}) => {
  const classes = useStyles();
  const { T } = useIntl();
  const { sm } = useMediaQuery();
  const open = showProp || !!openProp;
  const dispatch = useDispatch();
  const { name: pageName, category } = usePage();

  React.useEffect(() => {
    if (name && open) {
      dispatch(
        track('Open dialog', {
          name,
          pageName,
          pageCategory: category,
        }),
      );
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen !== undefined ? fullScreen : !sm}
      onClose={onClose}
      onEnter={onEnter}
      maxWidth={maxWidth || 'xs'}
      data-test-locator={
        dataTestLocator ? `${dataTestLocator} Dialog` : 'Dialog Container'
      }
      {...props}
    >
      {title || onClose ? (
        <DialogTitle>
          <Box pr={2}>{title}</Box>
          {onClose && (
            <IconButton
              className={classes.closeButton}
              aria-label={T('close')}
              onClick={onClose}
              data-test-locator={
                dataTestLocator
                  ? `${dataTestLocator} Close Button`
                  : 'Dialog Close Button'
              }
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      ) : null}

      {content ? (
        <DialogContent
          dividers={dividers}
          className={cx({ [classes.noPadding]: noContentPadding })}
        >
          {isString(content) ? (
            <DialogContentText>{content}</DialogContentText>
          ) : (
            content
          )}
        </DialogContent>
      ) : null}

      {dialogActions && (
        <DialogActions className={classes.actions}>
          {dialogActions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DialogNext;
